/**
 * Product storage assignment — the pure logic behind the "designated location"
 * prompt after Print Label (Create New Product spec) and the New Location
 * picker. Pure and dependency-free so the picking rules are unit-testable;
 * the dialogs fetch and this module decides.
 *
 * Persistence lives server-side: `PATCH
 * /inventory/storage-nodes/products/:productId/location` stores the
 * assignment, occupancy flows out of the shared `occupied` annotations this
 * module already reads, and the audit trail is written by the backend.
 */

import type { ProductCatalogEntry } from '@/types/products'
import type { ApiStorageNode, CapacitySummaryRow, StorageCondition } from '@/types/storage'
import { OCCUPIABLE_KIND } from '@/types/storage'
import { effectiveConditionFor } from './storageBrowse'

/**
 * Map a product catalog entry's free-text `storageTemperature` onto a validated
 * storage condition. null means "could not tell", and the caller must then say
 * conditions were NOT checked (same contract as
 * `FreeSpaceResult.conditionsChecked`).
 *
 * No longer what the product location dialogs filter by: they use the entry's
 * `storageCondition`, which the tenant maps explicitly (Configure → Products →
 * Storage Temp Mapping) and the server enforces. This reading is a guess.
 *
 * A PARSED NUMBER comes first, because the ISBT 128 catalogue states almost
 * every temperature as a bound: `<=-150C`, `<=-80C`, `<=-18C`, `20-24C`. The
 * previous token-matching version answered `null` for 14 of the 20 values in
 * the standard, and — worse — answered REFRIGERATED for `20-24C`, because its
 * `4c` alternative matched inside "2**4C**". A wrong condition is worse than an
 * unknown one: it sends a room-temperature product to a fridge and reports the
 * condition as checked.
 *
 * Token matching is kept as the fallback for the hand-entered vocabulary that
 * predates the ISBT import ("rt", "ref", "LN2", "-80").
 */
export function conditionForStorageTemp(storageTemp: string | undefined): StorageCondition | null {
  const t = (storageTemp ?? '').trim().toLowerCase()
  if (!t) return null

  // Explicit phrases win over any number in the same string.
  if (/immers/.test(t)) return 'LN2_IMMERSION'
  if (/controlled.rate|\bcrf\b/.test(t)) return 'CONTROLLED_RATE_FREEZER'
  if (/ln2|liquid nitrogen|n2 liquid|vapor|vapour/.test(t)) return 'VAPOR_PHASE_LN2'

  const celsius = readCelsius(t)
  if (celsius !== null) return conditionForCelsius(celsius)

  // No number to read — the pre-ISBT free-text vocabulary.
  if (/cryo/.test(t)) return 'VAPOR_PHASE_LN2'
  if (/ultra|\bulf\b|mechanical|frozen|frz/.test(t)) return 'ULTRA_LOW_MECHANICAL'
  if (/^ref|refrig|fridge/.test(t)) return 'REFRIGERATED'
  if (/^rt$|room|ambient|\bcrt\b|controlled room/.test(t)) return 'CONTROLLED_ROOM_TEMP'
  return null
}

/**
 * The one temperature a string is about, in °C, or null if it names none.
 *
 * The catalogue writes temperatures two ways and they do not mean the same
 * thing: `<=-150C` is a BOUND (one number, the limit) and `20-24C` is a RANGE
 * (two numbers, a band to stay inside). So one number is used as-is and two are
 * reduced to their midpoint — which is what puts `20-24C` in a room-temperature
 * unit instead of a fridge.
 *
 * A `-` counts as a minus sign only when a digit does not precede it, so
 * `20-24C` reads as 20 and 24 rather than 20 and -24.
 */
function readCelsius(text: string): number | null {
  const values: number[] = []
  const pattern = /(-|−|–)?\s*(\d+(?:\.\d+)?)\s*(?:°\s*)?c?\b/g
  for (const m of text.matchAll(pattern)) {
    const dash = m[1]
    // `m.index` points at the sign when there is one; look at what precedes it.
    const before = text.slice(0, m.index).trimEnd().slice(-1)
    const negative = !!dash && !/\d/.test(before)
    values.push(negative ? -Number(m[2]) : Number(m[2]))
  }
  if (values.length === 0) return null
  if (values.length === 1) return values[0]
  return (Math.min(...values) + Math.max(...values)) / 2
}

/**
 * Which equipment holds a given temperature.
 *
 * The boundaries follow the storage conditions this app models, not a clinical
 * standard. -100C is the vapour-phase cut because a mechanical ultra-low
 * freezer bottoms out near -86C, and the catalogue carries `<=-120C` through
 * `<=-150C` codes that none of them reaches.
 *
 * The -40C to 0C band is deliberately unmapped: that is a plasma freezer, which
 * is not one of the conditions here, and null makes the caller report that
 * conditions were not checked rather than name the wrong unit.
 */
function conditionForCelsius(celsius: number): StorageCondition | null {
  if (celsius <= -100) return 'VAPOR_PHASE_LN2'
  if (celsius <= -40) return 'ULTRA_LOW_MECHANICAL'
  if (celsius < 0) return null
  if (celsius <= 10) return 'REFRIGERATED'
  if (celsius <= 30) return 'CONTROLLED_ROOM_TEMP'
  return null
}

/**
 * The units a product may go in, given the condition its catalog temperature is
 * mapped to. Exact match on the unit's condition — the server's put-away rule —
 * so a unit with no condition declared is hidden too. `requires` null (an
 * unmapped or blank temperature) allows every unit.
 *
 * `hidden` is the count kept out, for the line saying why the list is short.
 *
 * Unit-level only. A root with no condition of its own may still hold
 * conditioned racks or boxes, but the capacity row does not say so, so such a
 * unit stays hidden for a mapped product; the slot filter in `freePositions`
 * is what checks the condition a position actually inherits.
 */
export function filterUnitsByCondition(
  units: CapacitySummaryRow[],
  requires: StorageCondition | null,
): { eligible: CapacitySummaryRow[]; hidden: number } {
  if (!requires) return { eligible: units, hidden: 0 }
  const eligible = units.filter(u => u.condition === requires)
  return { eligible, hidden: units.length - eligible.length }
}

/**
 * Pick the storage unit to suggest: the first unit (capacity-summary order,
 * which is the server's stable order) that has room, is in service, and — when
 * a condition is required — is operated at that condition. No unit at the required condition
 * means null, not a fallback: the server would refuse any other unit.
 * `conditionMatched` is false only when nothing was required.
 */
export function pickUnit(
  units: CapacitySummaryRow[],
  requires: StorageCondition | null,
): { unit: CapacitySummaryRow; conditionMatched: boolean } | null {
  // Never suggest a unit that is out of service or mid-maintenance: the server
  // refuses put-away there, so the suggestion would only fail at Confirm.
  const withRoom = units.filter(u => u.remaining > 0 && !u.unavailable)
  if (requires) {
    const matched = withRoom.find(u => u.condition === requires)
    return matched ? { unit: matched, conditionMatched: true } : null
  }
  const any = withRoom[0]
  return any ? { unit: any, conditionMatched: false } : null
}

/**
 * First free POSITION beneath a unit, in path order (subtree order): active,
 * not blocked, not occupied, and not the migration holding area — the same
 * exclusions the capacity figures make. With `requires`, only a position whose
 * effective condition (its nearest declaring ancestor) matches, as the server
 * checks it.
 */
export function pickFreePosition(
  subtree: ApiStorageNode[],
  requires: StorageCondition | null = null,
): ApiStorageNode | null {
  return freePositions(subtree, 1, requires)[0] ?? null
}

/**
 * Every free position beneath a unit, for the New Location picker. With
 * `requires`, a VAPOR product is not offered the LN2_IMMERSION section inside a
 * VAPOR dewar: the unit's own condition is not the slot's.
 */
export function freePositions(
  subtree: ApiStorageNode[],
  limit = 50,
  requires: StorageCondition | null = null,
): ApiStorageNode[] {
  const free: ApiStorageNode[] = []
  for (const n of subtree) {
    if (
      n.kind === OCCUPIABLE_KIND &&
      n.active &&
      !n.blocked &&
      !n.occupied &&
      !n.isHoldingArea &&
      (!requires || effectiveConditionFor(n, subtree) === requires)
    ) {
      free.push(n)
      if (free.length >= limit) break
    }
  }
  return free
}

/** What a product location dialog may offer, once its catalog entries are known. */
export type StorageRequirement =
  | { status: 'loading' }
  /** The catalog entries could not be read; set by the caller, never by `resolveRequirement`. */
  | { status: 'error'; message: string }
  /** The products' entries map to different conditions — place them one at a time. */
  | { status: 'conflict' }
  | { status: 'ready'; requiredCondition: StorageCondition | null; storageTemp: string | undefined }

/**
 * The storage requirement for products made from these catalog entries.
 *
 * `loading` until the entries resolve, so a dialog never says "any unit
 * allowed" for a product whose mapping it has not read yet — the server would
 * then refuse the slot. An entry the server did not return counts as unmapped,
 * which is also how the server treats it.
 */
export function resolveRequirement(
  catalogEntryIds: string[],
  byId: Map<string, Pick<ProductCatalogEntry, 'storageCondition' | 'storageTemperature'>>,
  loading: boolean,
): StorageRequirement {
  if (loading) return { status: 'loading' }
  const entries = [...new Set(catalogEntryIds)].map(id => byId.get(id))
  const conditions = new Set(entries.map(e => e?.storageCondition ?? null))
  if (conditions.size > 1) return { status: 'conflict' }
  const first = entries[0]
  return {
    status: 'ready',
    requiredCondition: first?.storageCondition ?? null,
    storageTemp: first?.storageTemperature || undefined,
  }
}

/**
 * Temporary/staging locations ("Temp Location could be part of the New
 * Location as some of options"): recognized by "TEMP" or "STAGING" in the
 * node's code or name, at the node or anywhere on its path.
 *
 * TODO(backend): this belongs as an `isTemporary` flag on `StorageNode`; the
 * name convention is a stopgap so labs can designate temp spots today.
 */
export function isTempLocation(node: Pick<ApiStorageNode, 'code' | 'name' | 'path'>): boolean {
  const haystack = `${node.path} ${node.name ?? ''}`.toLowerCase()
  return /temp|staging/.test(haystack)
}

